/**
 * @file Renders the schedule form's submit control, including the update/enable/disable/delete menu.
 */
import type { ComponentProps } from "react";
import { DeleteOutlined, DownOutlined, ExclamationCircleFilled, PauseCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { App, Button, Dropdown } from "antd";

import type { ScheduleStatus } from "@api/types";
import { HOLD as SCHEDULE_STATUS_HOLD } from "@api/services/transactions/shapes/ScheduleStatusEnum";

const LABEL_SUBMIT = "Publish";
const LABEL_UPDATE = "Update";
const LABEL_DELETE_SCHEDULE = "Delete";
const LABEL_ENABLE_SCHEDULE = "Run";
const LABEL_DISABLE_SCHEDULE = "Pause";
const LABEL_CONFIRM_DELETE_SCHEDULE_TITLE = "Delete Schedule";
const LABEL_CONFIRM_DELETE_SCHEDULE_CONTENT = "Are you sure you want to delete this schedule? This action cannot be undone.";
const LABEL_YES = "Yes";

type Props = {
  scheduleStatus: ScheduleStatus;
  isUpdate: boolean;
  isLoading: boolean;
  onDelete: () => void;
  onEnable: () => void;
  onDisable: () => void;
};

type SubmitDropdownButtonProps = Omit<
  ComponentProps<typeof Dropdown.Button>,
  "children"
>;

type SubmitPrimaryButtonProps = Omit<ComponentProps<typeof Button>, "children">;

/**
 * Renders a plain submit button for a new schedule, or an update/enable/disable/delete menu for an existing one.
 */
const SubmitButton = ({
  isUpdate,
  isLoading,
  onDelete,
  scheduleStatus,
  onEnable,
  onDisable,
}: Props) => {
  const { modal } = App.useApp();

  const isHold = scheduleStatus === SCHEDULE_STATUS_HOLD;

  /**
   * Opens a confirmation dialog before deleting the schedule.
   */
  const confirmDelete = () => {
    modal.confirm({
      icon: <ExclamationCircleFilled />,
      title: LABEL_CONFIRM_DELETE_SCHEDULE_TITLE,
      content: LABEL_CONFIRM_DELETE_SCHEDULE_CONTENT,
      okText: LABEL_YES,
      okType: "danger",
      /**
       * Deletes the schedule once the user confirms.
       */
      onOk: () => onDelete(),
    });
  };

  if (isUpdate) {
    const enableItem = {
      key: "enable",
      icon: <PlayCircleOutlined />,
      label: LABEL_ENABLE_SCHEDULE,
      onClick: onEnable,
    };

    const disableItem = {
      key: "disable",
      icon: <PauseCircleOutlined />,
      label: LABEL_DISABLE_SCHEDULE,
      onClick: onDisable,
    };

    const deleteItem = {
      key: "delete",
      icon: <DeleteOutlined />,
      label: LABEL_DELETE_SCHEDULE,
      danger: true,
      /**
       * Opens the delete confirmation dialog.
       */
      onClick: () => confirmDelete(),
    };

    const dropdownButtonProps: SubmitDropdownButtonProps = {
      type: "primary",
      icon: <DownOutlined />,
      menu: {
        items: isHold 
          ? [ enableItem, deleteItem ] 
          : [ disableItem, deleteItem ],
      },
      htmlType: "submit",
      loading: isLoading,
      disabled: isLoading,
      trigger: ["click"],
    };

    return (
      <Dropdown.Button {...dropdownButtonProps}>
        {LABEL_UPDATE}
      </Dropdown.Button>
    );
  }

  const buttonProps: SubmitPrimaryButtonProps = {
    type: "primary",
    htmlType: "submit",
    loading: isLoading,
    disabled: isLoading,
  };

  return (
    <Button {...buttonProps}>
      {LABEL_SUBMIT}
    </Button>
  );
};

export default SubmitButton;
